/* eslint-disable max-len */

import { mergeMap } from 'rxjs/operators/mergeMap';
import { map } from 'rxjs/operators/map';
import { withLatestFrom } from 'rxjs/operators/withLatestFrom';

import get from 'lodash.get';
import _reduce from 'lodash.reduce';
import camelCase from 'camel-case';
import { isValid } from 'redux-form';

import { CartTypeQuery } from '../../graphql/cart';
import { DOWNLOAD_ONLY } from '../../constants/cartTypes';

/*

  HELPERS

*/
const getErrors = (syncErrors, values) =>
  Object.keys(syncErrors).reduce((acc, field) => {
    if (!values[field]) {
      acc[field] = syncErrors[field];
    }

    return acc;
  }, {});

const isGroupValid = (syncErrors, group) =>
  !Object.keys(syncErrors).some((item) => {
    if (item.indexOf(group) > -1) {
      return item;
    }

    return false;
  });

const modifyFormFieldKey = (key, val, result, form) => {
  const isForm = key.indexOf(`${form}_`) > -1;

  if (!isForm) return {};

  const stripped = key.replace(`${form}_address`, '');
  const isStreetField = key.indexOf('street') > -1;
  const newKey = camelCase(stripped);

  if (!newKey.length) return {};

  if (isStreetField) {
    const prevStreet = result.street || [];

    return {
      street: [...prevStreet, val],
    };
  }

  return {
    [newKey]: val,
  };
};

const extractFormValuesFor = (values, form) =>
  _reduce(
    values,
    (result, val, key) => ({
      ...result,
      ...modifyFormFieldKey(key, val, result, form),
    }),
    {}
  );

const mapFormValuesToAddress = (values) => ({
  shipping: extractFormValuesFor(values, 'shipping'),
  billing: extractFormValuesFor(values, 'billing'),
});

const mapScreenState = (state$, apolloClient) => (stream) =>
  stream.pipe(
    mergeMap(() =>
      apolloClient.query({
        query: CartTypeQuery,
      })
    ),
    withLatestFrom(state$),
    map(([{ data }, state]) => {
      const isDownloadOnly = get(data, 'user.cart.type') === DOWNLOAD_ONLY;
      const cartId = get(data, 'user.cart.id');

      const { syncErrors = {}, values = {}, initial = {} } = get(
        state,
        'form.checkout',
        {}
      );

      const { isAddAddressShown } = state.screens;
      const isSameShipping = get(
        state,
        'form.checkout.values.same_shipping',
        false
      );
      const billingAddress = get(
        state,
        'form.checkout.values.billing_address',
        null
      );
      const shippingAddress = get(
        state,
        'form.checkout.values.shipping_address',
        undefined
      );

      const isTotallyNewUser = !billingAddress && !shippingAddress;

      const mappedFormValuesForMutation = mapFormValuesToAddress(values);
      const hasChangedShippingSelection =
        shippingAddress && shippingAddress !== initial.shipping_address;
      const hasChangedBillingSelection =
        billingAddress && billingAddress !== initial.billing_address;

      const errors = getErrors(syncErrors, values);
      const errorFields = Object.keys(errors);
      const firstField = errorFields[0];
      const firstErrorMessage = errors[firstField];
      const isFormValid = isValid('checkout')(state);

      const isShippingGroupValid = isGroupValid(syncErrors, 'shipping');
      const isBillingGroupValid = isGroupValid(syncErrors, 'billing');

      return {
        cartId,
        isShippingGroupValid,
        isBillingGroupValid,
        isDownloadOnly,
        isSameShipping,
        isTotallyNewUser,
        isAddAddressShown,
        hasChangedShippingSelection,
        hasChangedBillingSelection,
        billingAddress,
        shippingAddress,
        mappedFormValuesForMutation,
        firstErrorMessage,
        errorFields,
        isFormValid,
      };
    })
  );

export default mapScreenState;
